import { useState } from "react";

import { getText, interpolateString } from "shared/lib";
import { type Message } from "shared/apiClient";
import {
  treeName,
  TreePropertyType,
  type CircumferenceTreePropertyDescriptor,
  type DBHTreePropertyDescriptor,
  type HeightTreePropertyDescriptor,
  type MeasurementTypeTreePropertyDescriptor,
  type PruningQuantityTreePropertyDescriptor,
  type RemoveOrPruneTreePropertyDescriptor,
  type SizeEstimateTreePropertyDescriptor,
  type TrunkTypeTreePropertyDescriptor,
  CanopySizeTreePropertyDescriptor,
} from "@/components/treeModule/lib";

import { createMessageEvent, useChannel } from "shared/channel";
import {
  CaptureEventType,
  useAccount,
  useCaptureEvent,
  useNoticeboardData,
  useTreeModuleExpertResult,
} from "@/hooks";

import {
  RemovingPruningTreesContainer,
  type RemovingPruningTreesContainerProps,
} from "./RemovingPruningTreesContainer";
import { QuestionLoader } from "@/components/wizard/questionLoader";

export interface RemovingPruningTreesModuleStepProps {
  disabled?: boolean;
  message: Message;
  showLoading?: boolean;
}

export function RemovingPruningTreesModuleStep({
  disabled,
  message,
  showLoading,
}: RemovingPruningTreesModuleStepProps) {
  const { captureEvent } = useCaptureEvent();

  const { channel } = useChannel();

  const { conversation } = useAccount();
  const { noticeboardData } = useNoticeboardData();
  const treeModuleExpertResult = useTreeModuleExpertResult(
    conversation,
    noticeboardData,
  );

  const [submitting, setSubmitting] = useState(false);

  const handleClickNext: RemovingPruningTreesContainerProps["onClickNext"] = (
    treeData,
  ) => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      const text: Array<string> = [];
      if (["yes", "unsure"].includes(treeData.areYouRemovingOrPruning)) {
        if (treeData.areYouRemovingOrPruning === "unsure" && treeData.treeDescriptors.length === 0) {
          text.push(
            getText(
              "I might need to remove or prune some trees/vegetation, but I don't know their details or have chosen not to specify them."
            )
          );
        } else {
          text.push(
            interpolateString(
              "{{prefix}} remove or prune some trees/vegetation, with the following properties:",
              {
                prefix:
                  treeData.areYouRemovingOrPruning === "unsure"
                    ? getText("I might need to")
                    : getText("I will need to"),
              },
            ),
          );
        }

        treeData.treeDescriptors.forEach((treeDescriptor) => {
          const propertyMap = treeDescriptor.properties;

          const measurementType = propertyMap.get(
            TreePropertyType.measurement_type,
          )?.value as
            | MeasurementTypeTreePropertyDescriptor["value"]
            | undefined;

          const trunkType = propertyMap.get(TreePropertyType.trunk_type)
            ?.value as TrunkTypeTreePropertyDescriptor["value"] | undefined;

          const size = propertyMap.get(TreePropertyType.size)?.value as
            | SizeEstimateTreePropertyDescriptor["value"]
            | undefined;

          const removeOrPruneProperty = propertyMap.get(
            TreePropertyType.remove_or_prune,
          ) as RemoveOrPruneTreePropertyDescriptor | undefined;

          // tree name
          text.push(`- ${treeName(treeDescriptor)}`);

          // measurement type
          text.push(
            interpolateString("    - Measurement type: {{measurementType}}", {
              measurementType,
            }),
          );

          // trunk type
          let trunkTypeDisplay: string = trunkType || "";
          if (trunkType === "single") {
            trunkTypeDisplay = "Single-stem tree";
          } else if (trunkType === "multi") {
            trunkTypeDisplay = "Multi-stem tree";
          } else if (trunkType === "vegetation") {
            trunkTypeDisplay = "Vegetation only";
          }

          text.push(
            interpolateString("    - Type: {{trunkType}}", {
              trunkType: trunkTypeDisplay,
            }),
          );

          // remove or prune
          if (removeOrPruneProperty?.value === "remove") {
            text.push(getText("    - I will be removing this tree/vegetation"));
          } else if (removeOrPruneProperty?.value === "prune") {
            text.push(getText("    - I will be pruning this tree/vegetation"));
          }

          const dbhProperty = propertyMap.get(TreePropertyType.dbh) as
            | DBHTreePropertyDescriptor
            | undefined;
          if (dbhProperty && trunkType !== "vegetation") {
            text.push(
              interpolateString(
                "    - Diameter at {{distanceAboveGround}} above ground level: {{dbh}} m",
                {
                  distanceAboveGround: dbhProperty.distanceAboveGround,
                  dbh: dbhProperty.value,
                },
              ),
            );
          }

          const circumferenceProperty = propertyMap.get(
            TreePropertyType.circumference,
          ) as CircumferenceTreePropertyDescriptor | undefined;
          if (circumferenceProperty && trunkType !== "vegetation") {
            text.push(
              interpolateString(
                "    - Circumference at ground level: {{groundLevelCircumference}} m",
                {
                  groundLevelCircumference:
                    circumferenceProperty.groundLevelCircumference,
                },
              ) as string,
            );
            text.push(
              interpolateString(
                "    - Circumference at {{distanceAboveGround}} above ground level: {{circumference}} m",
                {
                  distanceAboveGround:
                    circumferenceProperty.distanceAboveGround,
                  circumference: circumferenceProperty.value,
                },
              ),
            );
            // calculate circumference from diameter if not provided
          } else if (dbhProperty?.value) {
            const dbhValue = parseFloat(dbhProperty.value);
            const calculatedCircumference = (dbhValue * Math.PI).toFixed(2);
            text.push(
              interpolateString(
                "    - Circumference at {{distanceAboveGround}} above ground level: {{circumference}} m (calculated from diameter)",
                {
                  distanceAboveGround: dbhProperty.distanceAboveGround,
                  circumference: calculatedCircumference,
                },
              ),
            );
          }

          // height (estimate flow)
          if (measurementType === "estimate") {
            switch (size) {
              case "small":
                text.push(getText("    - Height: less than 5 metres"));
                break;
              case "medium":
                text.push(getText("    - Height: 5-10 metres"));
                break;
              case "large":
                text.push(getText("    - Height: over 10 metres"));
                break;
            }
          }

          // height (detailed flow)
          const heightProperty = propertyMap.get(TreePropertyType.height) as
            | HeightTreePropertyDescriptor
            | undefined;
          if (measurementType === "detailed" && trunkType !== "vegetation") {
            text.push(
              interpolateString("    - Height: {{treeHeight}} metres", {
                treeHeight: heightProperty?.value,
              }),
            );
          }

          // canopy diameter (estimate flow)
          if (measurementType === "estimate") {
            switch (size) {
              case "small":
                text.push(getText("    - Canopy diameter: Less than 4 metres"));
                break;
              case "medium":
                text.push(getText("    - Canopy diameter: 4-8 metres"));
                break;
              case "large":
                text.push(getText("    - Canopy diameter: Over 8 metres"));
                break;
            }
          }

          // canopy size greater than 4m (detailed flow)
          const canopySizeProperty = propertyMap.get(
            TreePropertyType.canopy_size,
          ) as CanopySizeTreePropertyDescriptor | undefined;
          if (
            measurementType === "detailed" &&
            (trunkType === "single" || trunkType === "multi")
          ) {
            if (canopySizeProperty?.value === "yes") {
              text.push(getText("    - Canopy diameter: Over 4 metres"));
            } else if (canopySizeProperty?.value === "no") {
              text.push(getText("    - Canopy diameter: Less than 4 metres"));
            }
          }

          // species
          text.push(
            interpolateString("    - Species: {{species}}", {
              species: propertyMap.get(TreePropertyType.species)?.value,
            }),
          );

          // pruning quantity (only for pruning)
          if (removeOrPruneProperty?.value === "prune") {
            const pruningQuantity = propertyMap.get(
              TreePropertyType.pruning_quantity,
            )?.value as PruningQuantityTreePropertyDescriptor["value"];
            switch (pruningQuantity) {
              case "minor":
                text.push(
                  getText(
                    "    - Pruning quantity: minor trimming only (less than 10% of the foliage and branches)",
                  ),
                );
                break;
              case "moderate":
                text.push(
                  getText(
                    "    - Pruning quantity: moderate reduction (up to 30% of the foliage and branches)",
                  ),
                );
                break;
              case "significant":
                text.push(
                  getText(
                    "    - Pruning quantity: significant reduction (over a third of the foliage and/or lopping large branches)",
                  ),
                );
                break;
            }
          }

          // reason
          text.push(
            interpolateString("    - Reason: {{removingOrPruningReason}}", {
              removingOrPruningReason: propertyMap.get(TreePropertyType.reason)
                ?.value,
            }),
          );

          // property boundary
          if (
            (measurementType === "estimate" && size === "medium") ||
            size === "large"
          ) {
            text.push(
              interpolateString(
                "    - Is the tree within 6 metres of the property's front boundary, or within 4.5 metres of its rear boundary?: {{response}}",
                {
                  response: propertyMap.get(TreePropertyType.property_boundary)
                    ?.value,
                },
              ),
            );
          } else if (
            measurementType === "detailed" &&
            (trunkType === "single" || trunkType === "multi")
          ) {
            text.push(
              interpolateString(
                "    - Is the tree within 6 metres of the property's front boundary, or within 4.5 metres of its rear boundary?: {{response}}",
                {
                  response: propertyMap.get(TreePropertyType.property_boundary)
                    ?.value,
                },
              ),
            );
          }

          text.push("\n");
        });
      } else {
        text.push(
          getText("I will not be removing or pruning any trees or vegetation."),
        );
      }

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation?.id || "",
        text: text.join("\n"),
      });

      console.log(
        "<RemovingPruningTreesModuleStep> handleClickNext()",
        messageDescriptor,
      );
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  if (showLoading) {
    return <QuestionLoader />;
  }

  return (
    <RemovingPruningTreesContainer
      conversation={conversation}
      disabled={disabled}
      onClickNext={handleClickNext}
      showLoading={showLoading}
      state={conversation?.address_data?.state || ""}
      submitting={submitting}
      treeModuleExpertResult={treeModuleExpertResult}
    />
  );
}
